import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import { ICONS } from 'consts/consts';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, PopoverBody, Row } from 'reactstrap';
import {
  DevelopmentPlan,
  DevelopmentPlanStatus,
  getDevelopmentPlanStatus,
  getTitleForDevelopmentPlan,
} from 'utils/models/DevelopmentPlan';
import { useOrganizationSettings } from 'utils/util/hooks';
import {
  comparePeople,
  getId,
  getPrettyDate,
  stripHtml,
} from 'utils/util/util';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import {
  FORMAT_AVATAR_ONLY,
  FORMAT_AVATAR_WITH_TITLE,
} from 'views/Widgets/People/Filters/common';
import DevelopmentPlansFilterBar from './DevelopmentPlansFilterBar';

interface Props {
  developmentPlans: DevelopmentPlan[];
  hideHeaderControls?: boolean;
  filterStatuses: string[];
  toggleFilterStatuses: (status: string) => void;
}

const DevelopmentPlansTable: FC<Props> = ({
  developmentPlans,
  hideHeaderControls,
  filterStatuses,
  toggleFilterStatuses,
}) => {
  const { formatMessage, locale } = useIntl();
  const settings = useOrganizationSettings();

  const formattedDevelopmentPlans = useMemo(
    () =>
      developmentPlans.map((dp) => ({
        ...dp,
        type: getTitleForDevelopmentPlan(dp, settings),
      })),
    [developmentPlans, settings]
  );

  // It's a post processor, not to mess with the generation of suggestions
  const statusPostFilteringRow = useCallback(
    (developmentPlan) =>
      !filterStatuses.length || filterStatuses.includes(developmentPlan.status),
    [filterStatuses]
  );

  return (
    <FilterablePeopleTable
      // we may use arrays in values, e.g. for list of direct reports, so this must be false
      arrayValuesUsedForFormatting={false}
      tableClassName="calibration-table"
      hideHeader={hideHeaderControls}
      headerContent={
        <Row className="align-items-center">
          <Col className="col-auto ps-2">
            <DevelopmentPlansFilterBar
              filterStatuses={filterStatuses}
              toggleFilterStatuses={toggleFilterStatuses}
            />
          </Col>
          <Col></Col>
        </Row>
      }
      rows={formattedDevelopmentPlans}
      getUniqueRowId={getId}
      columns={[
        {
          name: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.name.candidate_person',
            defaultMessage: 'Candidate',
          }),
          field: 'candidate_person',
          isPerson: true,
          format: FORMAT_AVATAR_WITH_TITLE,
          csvName: ['Candidate name', 'Candidate email'],
          csvFormat: (c) => ({
            'Candidate name': c.full_name,
            'Candidate email': c.email,
          }),
          getFilterDisplayValue: (c) => c.full_name,
          nameTransformerFunction: (name) =>
            formatMessage(
              {
                id: 'app.views.development_plans.development_plans_dashboard.name.candidate_person.filter',
                defaultMessage: '{name} as candidate',
              },
              { name }
            ),
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.candidate_person"
              defaultMessage="Name, title, and link to resume of candidate"
            />
          ),
          sort: (a, b) =>
            comparePeople(a?.candidate_person, b?.candidate_person),
        },
        {
          name: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.name.supporter_person',
            defaultMessage: 'Supporter',
          }),
          field: 'supporter_person',
          isPerson: true,
          format: FORMAT_AVATAR_ONLY,
          csvFormat: (c) => c.email,
          getFilterDisplayValue: (c) => c.full_name,
          nameTransformerFunction: (name) =>
            formatMessage(
              {
                id: 'app.views.development_plans.development_plans_dashboard.name.supporter_person.filter',
                defaultMessage: '{name} as supporter',
              },
              { name }
            ),
          ifEmpty: '-',
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.supporter_person"
              defaultMessage="The manager that is supporting the development plan"
            />
          ),
          sort: (a, b) =>
            comparePeople(a?.supporter_person, b?.supporter_person),
        },
        {
          name: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.name.type',
            defaultMessage: 'Type',
          }),
          field: 'type',
          filterIcon: ICONS.TYPE,
        },
        {
          name: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.name.start_date',
            defaultMessage: 'Start date',
          }),
          field: 'start_date',
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.start_date"
              defaultMessage="When the development plan was started"
            />
          ),
          format: (date) => {
            return date ? getPrettyDate({ dateString: date, locale }) : '-';
          },
          hideFromFilters: true,
        },
        {
          name: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.name.end_date',
            defaultMessage: 'End date',
          }),
          field: 'end_date',
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.end_date"
              defaultMessage="When the development plan ends, if ever."
            />
          ),
          format: (date) => {
            return date ? getPrettyDate({ dateString: date, locale }) : '-';
          },
          hideFromFilters: true,
        },
        {
          name: (
            <span>
              <i className={ICONS.ACTIVITY} />
            </span>
          ),
          filterIcon: ICONS.ACTIVITY,
          hideFromFilters: true,
          filterDescription: 'Status',
          csvName: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.csv.status',
            defaultMessage: 'Status',
          }),
          csvFormat: (s: DevelopmentPlanStatus) =>
            getDevelopmentPlanStatus(s).name,
          field: 'status',
          format: (s: DevelopmentPlanStatus, uniqueIndexIdentifier) => {
            const status = getDevelopmentPlanStatus(s);
            return (
              <>
                <i
                  id={'status-' + uniqueIndexIdentifier}
                  className={status.icon}
                />
                <SafeUncontrolledPopover
                  trigger="hover"
                  placement="top"
                  target={'status-' + uniqueIndexIdentifier}
                >
                  <PopoverBody>
                    <div className="text-dark">
                      <FormattedMessage
                        id="app.views.development_plans.development_plans_dashboard.packet_popover_heading"
                        defaultMessage="<span>{heading}</span>"
                        values={{
                          heading: status.heading,
                          span: (chunks) => (
                            <span className="fw-bold">{chunks}</span>
                          ),
                        }}
                      />
                    </div>
                    <div>{status.description}</div>
                  </PopoverBody>
                </SafeUncontrolledPopover>
              </>
            );
          },
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.status"
              defaultMessage="Development plan status"
            />
          ),
        },
        {
          name: (
            <span>
              <i className={ICONS.ACKNOWLEDGED} />
            </span>
          ),
          filterIcon: ICONS.ACTIVITY,
          filterDescription: 'Candidate acknowledged',
          csvName: formatMessage({
            id: 'app.views.development_plans.development_plans_dashboard.csv.candidate_acknowledged_at',
            defaultMessage: 'Candidate acknowledged at',
          }),
          field: 'candidate_acknowledged_at',
          hideFromFilters: true,
          format: (
            datetime: Date | null,
            uniqueIndexIdentifier,
            _rowIndex,
            rowValue
          ) => {
            if (rowValue.candidate_can_manage) {
              const uniqueId =
                'candidate_acknowledged_at_' + uniqueIndexIdentifier;
              return (
                <div className="ms-0 text-muted" id={uniqueId}>
                  <FormattedMessage
                    id="app.views.development_plans.development_plans_dashboard.candidate_ack_na"
                    defaultMessage="n/a"
                  />
                  <SafeUncontrolledPopover
                    placement="left"
                    trigger="hover"
                    target={uniqueId}
                  >
                    <FormattedMessage
                      id="app.views.development_plans.development_plans_dashboard.candidate_ack_na.popover"
                      defaultMessage="The candidate can manage this plan. No need to acknowledge."
                    />
                  </SafeUncontrolledPopover>
                </div>
              );
            }
            return (
              <>
                {datetime && (
                  <>
                    <i
                      id={'status-' + uniqueIndexIdentifier}
                      className={ICONS.ACKNOWLEDGED}
                    />
                    <SafeUncontrolledPopover
                      trigger="hover"
                      placement="top"
                      target={'status-' + uniqueIndexIdentifier}
                    >
                      <PopoverBody>
                        <div className="text-dark">
                          <FormattedMessage
                            id="app.views.development_plans.development_plans_dashboard.candidate_acknowledged_at.popover"
                            defaultMessage="The candidate ackwnowledged at <span>{date}</span>"
                            values={{
                              date: getPrettyDate({
                                dateString: datetime,
                                locale,
                                includeTime: true,
                              }),
                              span: (chunks) => (
                                <span className="fw-bold">{chunks}</span>
                              ),
                            }}
                          />
                        </div>
                      </PopoverBody>
                    </SafeUncontrolledPopover>
                  </>
                )}
              </>
            );
          },
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.candidate_acknowledged_at"
              defaultMessage="Checked if the candidate acknowledged. Hover to see the date when this last happened."
            />
          ),
        },
        {
          name: formatMessage({
            id: 'app.views..development_plans.development_plans_dashboard.name.action',
            defaultMessage: 'Action',
          }),
          field: 'action',
          sortable: false,
          hideFromCSV: true,
          hideFromFilters: true,
          popoverContent: (
            <FormattedMessage
              id="app.views.development_plans.development_plans_dashboard.popover_content.action"
              defaultMessage="Review plans to change status, add comments, etc."
            />
          ),
        },
        {
          csvOnly: true,
          name: formatMessage({
            id: 'app.views..development_plans.development_plans_dashboard.name.description',
            defaultMessage: 'Description',
          }),
          field: 'description',
          csvFormat: (val) => stripHtml(val, true),
        },
      ]}
      // sort by newest first
      defaultSort={['id']}
      defaultSortAtoZ={false}
      filterType={FilterType.ADVANCED}
      postFilteringRow={statusPostFilteringRow}
    ></FilterablePeopleTable>
  );
};

export default DevelopmentPlansTable;
